import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useRequestData, usePrefs, useLabels, useFlags } from 'wsm-common-data';
import {
	PricingDetail,
	InstantEpriceContainer,
	SimplePrice,
	PriceSummary,
	getPriceSelection
} from 'wsm-pricing-display';
import { useDealerOptions, useDealerFees } from 'wsm-pricing-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { MyCarsCtas } from 'wsm-mycars-ctas';
import { isBrowser } from 'ws-scripts/modules/environment';
import { SRP, VDP } from '../constants/constants';
import { scrollToPricingDetails, titleCase } from '../utils/utility.js';
import { useGMPayments } from '../hooks/useGMPayments';

import '../global-sass/main.scss';

const Widget = () => {
	const { deviceType, widgetName, locale } = useRequestData();
	const prefs = usePrefs();
	const labels = useLabels();
	const flags = useFlags();

	const {
		account = { name: '' },
		pricingModuleData = {},
		userProfile,
		widgetInfo: { pageAlias, isComposer },
		wisVehicle: vehicleData,
		shouldPortalPricing = false,
		shouldFetchGMPayments = {
			eligible: false,
			includeDealerFees: false,
			reason: ''
		},
		isDREnabled: drEnabled = false,
		enableMyCars = false
	} = useSelector((state) => state);

	const [isDisplayingEPrice, setIsDisplayingEPrice] = useState(false);
	const [isScrollingSidebar, setIsScrollingSidebar] = useState(false);
	const [layout, setLayout] = useState(SRP);
	const [renderEpricePortal, setRenderEpricePortal] = useState(false);
	const [shouldRequestDealerOptions, setShouldRequestDealerOptions] =
		useState(false);
	const [shouldRequestDealerFees, setShouldRequestDealerFees] =
		useState(false);
	const [hasMounted, setHasMounted] = useState(false);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	const setUserProfile = useDispatch();

	const dealerOptions = useDealerOptions(
		Object.keys(pricingModuleData).length
			? pricingModuleData.vehicleId
			: null,
		locale,
		shouldRequestDealerOptions,
		widgetName
	);

	const dealerFees = useDealerFees(
		Object.keys(pricingModuleData).length
			? pricingModuleData.vehicleId
			: null,
		locale,
		shouldRequestDealerFees,
		widgetName
	);

	// Fetch GM payment data (controlled by server-side eligibility check)
	const {
		gmPayments,
		isLoading: isLoadingGMPayments,
		error: gmPaymentsError
	} = useGMPayments({
		vehicleId: pricingModuleData.vehicleId,
		accountId: pricingModuleData.accountId,
		locale,
		enabled: shouldFetchGMPayments.eligible,
		includeDealerFees: shouldFetchGMPayments.includeDealerFees,
		eligibilityReason: shouldFetchGMPayments.reason,
		labels
	});

	const pubsub = isBrowser && window?.DDC?.pubsub;

	const handleDealerOptionsMouseEnter = () => {
		setShouldRequestDealerOptions(true);
	};

	const handleDealerFeesMouseEnter = () => {
		setShouldRequestDealerFees(true);
	};

	const trackScrollingSidebar = useCallback(() =>
		// Tracks whether the sidebar (on some VDPs) is scrolling or not based on the height set
		{
			if (layout === 'VDP') {
				const vdpSidebar = document.querySelector('.vdp-sidebar');
				if (vdpSidebar) {
					setIsScrollingSidebar(vdpSidebar.style.top.length > 0);
				}
			}
		}, [layout]);

	const showEPrice = (id, vehicleId) => {
		if (id === vehicleId && id !== 'none') {
			setIsDisplayingEPrice(true);
		}
	};

	useEffect(() => {
		if (deviceType === 'DESKTOP') {
			window.addEventListener('scroll', trackScrollingSidebar);
		}

		if (
			window &&
			window.document &&
			window.document
				.getElementsByTagName('html')[0]
				.className.includes(VDP.toLowerCase())
		) {
			setLayout(VDP);
		}

		if (pubsub) {
			pubsub.subscribe('ws-vehicle-ctas/render-eprice-portal', () => {
				setRenderEpricePortal(true);
			});

			pubsub.subscribe('instant-ePrice-completed', () => {
				let profile = window.DDC.userProfile;
				if (!(profile && Object.keys(profile).length > 0)) {
					profile = null;
				}
				setUserProfile({
					type: 'SET_USER_PROFILE',
					payload: { userProfile: profile }
				});
			});
		}

		return () => {
			if (deviceType === 'DESKTOP') {
				window.removeEventListener('scroll', trackScrollingSidebar);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deviceType, setUserProfile, trackScrollingSidebar]);

	const {
		accountId = '',
		condition,
		incentives = {},
		inventoryData = {},
		priorityType,
		siteId = '',
		vehicleAccountId = '',
		vehicleId = ''
	} = pricingModuleData || {};

	const formattedCondition = titleCase(condition || '', ' ');

	const { dprice, eprice, ...restInventoryData } = inventoryData;

	const componentPricing = {
		dPrice: dprice,
		ePrice: eprice,
		...restInventoryData
	};

	const { finalPriceItem } = getPriceSelection(
		componentPricing,
		isDisplayingEPrice
	);

	useEffect(() => {
		if (pubsub && finalPriceItem) {
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-price',
				finalPriceItem.value
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [finalPriceItem]);

	useEffect(() => {
		if (pubsub && componentPricing) {
			pubsub?.publishDelayed(
				'ws-detailed-pricing/price-update',
				componentPricing
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [componentPricing]);

	// If the mobilePricingSummaryDisplay pref is set to 'full price stack' (which only happens on the Hyundai VDP),
	// then the Pricing Summary placement will contain all the content of the widget,
	// and nothing will be shown in the place where the widget would normally reside.
	// Pricing will not be shown in the mobile sticky footer (which is not present on the Hyundai VDP) either.
	// The code for this Hyundai-specific solution will be removed when we move Hyundai sites to the global responsive-sections VDP.
	const showFullPriceStackInPortalHyundai =
		prefs.mobilePricingSummaryDisplay === 'full price stack' &&
		deviceType === 'MOBILE' &&
		!isComposer;

	const [hyundaiFullPriceStackPortalContainer] =
		typeof document !== 'undefined' && showFullPriceStackInPortalHyundai
			? document.querySelectorAll('.ddc-vehicle-pricing-summary')
			: [];

	const [fullPriceStackPortalContainer] =
		typeof document !== 'undefined'
			? document.querySelectorAll('.vdp-price-purchase-detailed-pricing')
			: [];

	const [ePriceContainer] =
		typeof document !== 'undefined'
			? document.querySelectorAll('.eprice-react-portal')
			: [];

	const ePriceButton = !isDisplayingEPrice ? (
		<InstantEpriceContainer
			accountId={accountId}
			buttonSize={prefs.buttonSizeEPrice}
			buttonStyle={prefs.buttonStyleEPrice}
			ePriceFontWeight={prefs.ePriceFontWeight}
			instantEpricePreventMultipleEpriceLeads={
				prefs.instantEpricePreventMultipleEpriceLeads
			}
			instantEpriceShowAllAfterLeadSubmit={
				prefs.instantEpriceShowAllAfterLeadSubmit
			}
			pageAlias={pageAlias}
			pricing={componentPricing}
			showEPrice={(id) => showEPrice(id, vehicleId)}
			showInstantEpriceForSharedInventory={
				prefs.showInstantEpriceForSharedInventory
			}
			userProfile={userProfile}
			vehicleId={vehicleId}
			vehicleAccountId={vehicleAccountId}
		/>
	) : null;

	const showCollapsedPricing = prefs?.showCollapsedPricing === 'true';
	const enableAlteredCollapsedPrice =
		flags['ws-detailed-pricing-altered-collapsed-price'];

	const pricingDetailProps = {
		accountId,
		accountInfo: account,
		condition: formattedCondition,
		dealerOptions,
		fetchDealerOptionsCallback: handleDealerOptionsMouseEnter,
		dealerFees,
		fetchDealerFeesCallback: handleDealerFeesMouseEnter,
		gmPayments,
		gmPaymentsError,
		incentives,
		isDisplayingEPrice,
		isLoadingGMPayments,
		isScrollingSidebar,
		layout,
		prefs,
		pricing: componentPricing,
		vehicleAccountId,
		vehicleData,
		vehicleId
	};

	if (Object.keys(pricingModuleData).length === 0) {
		return null;
	}

	if (hyundaiFullPriceStackPortalContainer) {
		return (
			<ErrorBoundary newRelicPageAction="WS DETAILED PRICING HYUNDAI ERROR BOUNDARY">
				<>
					{ReactDOM.createPortal(
						<>
							<PricingDetail
								{...pricingDetailProps}
								hidePricingSummary
							/>
							{/*
									Target for Web Integration API. For information, see https://ghe.coxautoinc.com/DDC-WebPlatform/ddc-js-api.
									Note: If support for the restrictInsertLocationsForVehicles pref on ws-vehicle-ctas is required,
									then this div should ideally be added in ddc-js-api instead.
								*/}
							<div
								data-location="vehicle-payments vehicle-payments-custom"
								className="ddc-content web-api-added-placeholder hidden ws-detailed-pricing"
							/>
							{ePriceButton}
						</>,
						hyundaiFullPriceStackPortalContainer
					)}
				</>
			</ErrorBoundary>
		);
	}

	const showInlineCTA =
		showCollapsedPricing && prefs?.showCTAInCollapsedPricing === 'true';

	const showTrackPriceInline =
		hasMounted &&
		showCollapsedPricing &&
		prefs?.showTrackPriceInCollapsedPricing === 'true' &&
		!showInlineCTA &&
		enableMyCars &&
		!!vehicleId;

	const trackPriceButton = showTrackPriceInline ? (
		<MyCarsCtas
			isInLine
			buttonsToShow={['trackPrice']}
			uuid={vehicleId}
			accountId={accountId}
			siteId={siteId}
			image={vehicleData?.images?.[0]}
			bodyStyle={vehicleData?.bodyStyle}
			condition={condition}
			make={vehicleData?.make}
			model={vehicleData?.model}
			price={finalPriceItem?.value}
			priorityType={priorityType}
			stockNumber={vehicleData?.stockNumber}
			title={vehicleData?.title || []}
			trim={vehicleData?.trim}
			type={vehicleData?.type}
			vin={vehicleData?.vin}
			year={vehicleData?.year}
		/>
	) : null;

	let mainRenderComponent = <PricingDetail {...pricingDetailProps} />;
	if (showCollapsedPricing) {
		if (enableAlteredCollapsedPrice && PriceSummary) {
			mainRenderComponent = (
				<PriceSummary
					pricing={componentPricing}
					isDisplayingEPrice={isDisplayingEPrice}
					isDREnabled={drEnabled}
					onViewPriceDetails={scrollToPricingDetails}
				/>
			);
		} else {
			const collapsedPricingProps = {
				account,
				condition: formattedCondition,
				isGridLayout: false,
				pricing: componentPricing,
				isDisplayingEPrice,
				showExpandedPricing: false,
				setShowExpandedPricing: scrollToPricingDetails,
				isNewCarBoost: false,
				layout: VDP
			};
			mainRenderComponent = <SimplePrice {...collapsedPricingProps} />;
		}
	}

	if (showInlineCTA || showTrackPriceInline) {
		mainRenderComponent = (
			<div className="d-flex justify-content-between align-items-center flex-wrap">
				{mainRenderComponent}
				{showInlineCTA ? ePriceButton : trackPriceButton}
			</div>
		);
	}

	return (
		<ErrorBoundary newRelicPageAction="WS DETAILED PRICING ERROR BOUNDARY">
			<>
				{mainRenderComponent}
				{ePriceContainer &&
					renderEpricePortal &&
					!showInlineCTA &&
					ReactDOM.createPortal(ePriceButton, ePriceContainer)}
				{fullPriceStackPortalContainer &&
					shouldPortalPricing &&
					ReactDOM.createPortal(
						<>
							<h3
								id="vdp-price-purchase-detailed-pricing-heading"
								tabIndex={-1}
								className="widget-heading h2 mb-4"
							>
								{labels.get('VDP_PRICE_DETAILS')}
							</h3>
							<PricingDetail {...pricingDetailProps} />
						</>,
						fullPriceStackPortalContainer
					)}
			</>
		</ErrorBoundary>
	);
};

export default Widget;
